import type { ResolutionStatus as ResolutionStatuses } from '@library/types/resolutions/resolutionsList';
import { cn as classNames } from '@library/component-utils/cn';
import { resolutionStatusOptions } from '@bo-constants/resolutions/resolutionsList';
import { useMemo } from 'react';

export interface ResolutionStatusProps {
  className?: string;
  resolutionStatus: ResolutionStatuses;
}

const getStatusColorClassNames = ({
  status
}: {
  status: ResolutionStatuses;
}) => {
  switch (status) {
    case 'completed':
      return {
        bgClassName: 'bg-status-success/15 border-status-success',
        textClassName: ' text-status-success'
      };
    case 'discarded':
      return {
        bgClassName: 'bg-status-neutral/15 border-status-neutral',
        textClassName: ' text-status-neutral'
      };
    case 'draft':
      return {
        bgClassName: 'bg-status-info/15 border-status-info',
        textClassName: ' text-status-info'
      };
    case 'incomplete':
      return {
        bgClassName: 'bg-status-caution/15 border-status-caution',
        textClassName: ' text-status-caution'
      };
    case 'preprocessing':
    case 'processing':
      return {
        bgClassName: 'bg-status-warning/15 border-status-warning',
        textClassName: ' text-status-warning'
      };
    case 'unrecoverable_error':
      return {
        bgClassName: 'bg-status-failure/15 border-status-failure',
        textClassName: ' text-status-failure'
      };
    default:
      return {
        bgClassName: 'bg-status-info/15 border-status-info',
        textClassName: ' text-status-info'
      };
  }
};

export const ResolutionStatus = ({
  className = '',
  resolutionStatus
}: ResolutionStatusProps) => {
  const { bgClassName, textClassName } = useMemo(
    () => getStatusColorClassNames({ status: resolutionStatus }),
    [resolutionStatus]
  );

  const resolutionStatusName = useMemo(() => {
    return (
      resolutionStatusOptions.find(
        (resolutionStatusOption) =>
          resolutionStatusOption.value === resolutionStatus
      )?.name || resolutionStatus
    );
  }, [resolutionStatus]);

  return (
    <div
      className={classNames(
        'flex w-full items-center gap-2 rounded-md p-3',
        bgClassName,
        className
      )}
    >
      <div
        className={classNames(
          bgClassName,
          textClassName,
          'bg-current/100 size-3 rounded-full border border-solid'
        )}
      />
      <p className={classNames(textClassName, 'text-2xs xl:text-xs')}>
        {resolutionStatusName}
      </p>
    </div>
  );
};
